import React from 'react';
// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import I18n from 'i18n-js';

import {
  Core_Listing_RecommendedShopsRowQuery,
  Core_Listing_RecommendedShopsRowQueryVariables,
} from '@reverbdotcom/commons/src/gql/graphql';
import ShopCard, { COMPONENT_NAME, ShopCardFragment } from './shop_card';
import { SHOP_PAGE } from './shared/url_helpers';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { Tiles } from '@reverbdotcom/commons';
import { ClickedRecommendedShopCardEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import { EUROPEAN_UNION_COUNTRY_CODES } from '../lib/geo';

interface IProps {
  listingId?: string;
  browsePageId?: string;
  brandSlug?: string;
  categorySlugs?: string[];
}

type Shop = Core_Listing_RecommendedShopsRowQuery['listingsAggregationSearch']['shops'][0];

function RecommendedShopsRow({
  listingId = undefined,
  browsePageId = undefined,
  brandSlug = undefined,
  categorySlugs = [],
}: IProps) {
  const user = useUser();
  const { loading, data } = useQuery<Core_Listing_RecommendedShopsRowQuery, Core_Listing_RecommendedShopsRowQueryVariables>(
    RECOMMENDED_SHOPS_ROW_QUERY,
    {
      ssr: false,
      variables: {
        brandSlugs: brandSlug ? [brandSlug] : [],
        categorySlugs,
        itemRegion: user.countryCode ? user.countryCode : undefined,
      },
    },
  );

  const shops = loading || !data.listingsAggregationSearch ? [] : data.listingsAggregationSearch.shops;
  const title = useRowTitle(user);

  if (shops.length < 3) return null;

  return (
    <OverflowingRow
      title={title}
      collectionCount={shops.length}
    >
      <Tiles singleRow>
        {shops.map((shop, index) => (
          <CoreLink
            key={shop.id}
            to={shopLink(shop, brandSlug, categorySlugs)}
            clickEvent={buildClickEvent({
              shop,
              listingId,
              browsePageId,
              position: index,
              shopCount: shops.length,
            })}
          >
            <ShopCard shop={shop} />
          </CoreLink>
        ))}
      </Tiles>
    </OverflowingRow>
  );
}

function optionalCategory(categorySlug: string) {
  if (categorySlug) {
    return { category: categorySlug };
  }
  return {};
}

function shopLink(shop: Shop, brandSlug: string, categorySlugs: string[]) {
  const [firstCategorySlug] = categorySlugs;

  return SHOP_PAGE.expand({
    slug: shop.slug,
    query: {
      ...optionalCategory(firstCategorySlug),
      make: brandSlug,
    },
  });
}

const EXPERIMENT_COUNTRY_CODES = ['AU', 'BR', 'CA', 'NO', 'GB', ...EUROPEAN_UNION_COUNTRY_CODES];

function useRowTitle(user: Partial<IUser>): string {
  const userInExp = isExperimentEnabled(user, experiments.LOCALIZED_ROWS_ON_ITEM_PAGES);

  if (user.countryCode === 'GB') {
    return I18n.t('discovery.shopCard.recommendedShopsTitle.gb');
  } else if (userInExp && EXPERIMENT_COUNTRY_CODES.includes(user.countryCode)) {
    const countryTranslation = I18n.t(`discovery.shippingRegions.${user.countryCode}`);
    return I18n.t('discovery.shopCard.recommendedShopsTitle.inCountry', { country: countryTranslation });
  } else {
    return I18n.t('discovery.shopCard.recommendedShopsTitle.default');
  }
}

function buildClickEvent({
  shop,
  listingId,
  browsePageId,
  position,
  shopCount,
}: {
  shop: Shop,
  listingId: string,
  browsePageId: string,
  position: number,
  shopCount: number,
}): ClickedRecommendedShopCardEvent {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedRecommendedShopCard,
    listingId,
    browsePageId,
    position,
    shopCount,
    shopId: shop.id,
  };
}

export const RecommendedShopsRowListingFragment = gql`
  fragment RecommendedShopsRowListingFields on Listing {
    _id
    id
    brandSlug
    categories {
      _id
      slug
    }
  }
`;

const RECOMMENDED_SHOPS_ROW_QUERY = gql`
  query Core_Listing_RecommendedShopsRow(
    $itemRegion: String,
    $brandSlugs: [String],
    $categorySlugs: [String]
  ) {
    listingsAggregationSearch(input: {
      withAggregations: [SHOP_ID],
      preferredSeller: true,
      brandSlugs: $brandSlugs,
      categorySlugs: $categorySlugs,
      itemRegion: $itemRegion
    }) {
      shops {
        _id
        id
        slug
        address {
          _id
          countryCode
        }
        ...ShopCardFields
      }
    }
  }
  ${ShopCardFragment}
`;

export default RecommendedShopsRow;
